import React, { useCallback, useEffect, useState } from 'react';
import { App, Switch, Typography } from 'antd';

import {
	getNotificationCatalog, putNotificationSettings, resetNotificationSettings,
} from '../../services/notifications';

const { Text } = Typography;

/** keepr's two (keepr-api utils/notifications/formNotices.js KEYS). */
export const FORM_SUBMISSION_KEY = 'form-submission';
export const FORM_PENDING_OVER_KEY = 'form-pending-over';

// What each one does, in a line under its switch. The name is the API's.
const WHAT_IT_SAYS = {
	[FORM_SUBMISSION_KEY]: 'Each submission to any public form here tells this collection’s managers in keepr.',
	[FORM_PENDING_OVER_KEY]: 'Managers hear, in keepr and by email, when a form’s review queue is nearly full.',
};

/** A refusal of either switch, in words. */
function describeSwitchError(error, fallback) {
	const status = error?.response?.status;
	if (status === 403) return 'Only a person managing this collection, signed in, can change this — not an API key or an assistant.';
	if (!status) return 'keepr could not be reached. Check the connection and try again.';
	return error?.response?.data?.message || fallback;
}

/**
 * Collection settings → Public forms → **Notifications** (KPR-514; keepr-api
 * docs/SCHEMA.md § "Notifications about a public form"). keepr's two:
 * "New public form submission" and the review queue nearly full. They ARRIVE
 * PAUSED — no collection hears either until a person here turns it on — so
 * each has a switch for the whole collection:
 *
 *   on  → `PUT /api/notification-settings { scope: 'collection', key,
 *         muted: false }` — the opt-in row;
 *   off → `DELETE` that row. ⚑ Not a PUT of `muted: true`: a muting row is
 *         the one row a key may write, and the API reads only the opt-in as
 *         "on" — deleting puts the collection back where it started.
 *
 * The submission notice is in keepr only by default (a stranger could
 * otherwise spend every manager's mail budget), so while it is on each
 * manager has a second, personal switch: **Email me too**, a `scope: 'user'`
 * row for this collection that touches nobody else's mail.
 *
 * Rendered only when the API lists them: an API older than KPR-514, or one
 * whose owner has not yet written keepr's two, sends none, and a switch that
 * cannot land is worse than no switch.
 */
export default function PublicFormNotifications({ collectionId, notifications, onChanged }) {
	const { message } = App.useApp();
	const [busy, setBusy] = useState(null);
	// This person's own email for the submission notice: null until read.
	const [emailOn, setEmailOn] = useState(null);
	const rows = Array.isArray(notifications) ? notifications : [];
	const submission = rows.find(row => row.key === FORM_SUBMISSION_KEY);
	const submissionOn = submission?.on === true;

	useEffect(() => {
		if (!collectionId || !submissionOn) { setEmailOn(null); return undefined; }
		let active = true;
		getNotificationCatalog({ tier: 'user', collectionId })
			.then((catalog) => {
				if (!active) return;
				const row = catalog.rows.find(r => r?.key === FORM_SUBMISSION_KEY);
				setEmailOn(row?.channels?.email?.value === true);
			})
			// Unread is not off: the switch stays unset rather than guessing.
			.catch(() => { if (active) setEmailOn(null); });
		return () => { active = false; };
	}, [collectionId, submissionOn]);

	const toggle = useCallback(async (row, on) => {
		setBusy(row.key);
		try {
			if (on) await putNotificationSettings({ tier: 'collection', collectionId, key: row.key, muted: false });
			else await resetNotificationSettings({ tier: 'collection', collectionId, key: row.key });
			message.success(on ? `${row.name} is on for this collection.` : `${row.name} is off for this collection.`);
		} catch (error) {
			message.error(describeSwitchError(error, on ? 'The notification could not be turned on.' : 'The notification could not be turned off.'));
		} finally {
			setBusy(null);
			await onChanged?.();
		}
	}, [collectionId, message, onChanged]);

	const toggleEmail = useCallback(async (on) => {
		setBusy('email');
		try {
			await putNotificationSettings({ tier: 'user', collectionId, key: FORM_SUBMISSION_KEY, channels: { email: on } });
			setEmailOn(on);
			message.success(on ? 'You will get an email for each new submission too.' : 'You will hear about new submissions in keepr only.');
		} catch (error) {
			message.error(describeSwitchError(error, 'Your email setting could not be saved.'));
		} finally {
			setBusy(null);
		}
	}, [collectionId, message]);

	if (!rows.length) return null;

	return (
		<section className="form-panel public-form-notices" data-testid="public-form-notifications">
			<div className="micro-label form-panel__kicker">Notifications</div>
			<Text type="secondary" className="form-panel__hint">
				keepr can tell this collection’s managers what its public forms take in. Both start off; turn on the ones you want.
			</Text>
			<ul className="public-form-notices__list">
				{rows.map(row => (
					<li key={row.key} className="public-form-notices__item">
						<div className="public-form-notices__row">
							<div className="public-form-notices__text">
								<span className="public-form-notices__name" id={`public-form-notice-${row.key}`}>{row.name || row.key}</span>
								{WHAT_IT_SAYS[row.key] ? <span className="public-form-notices__meta">{WHAT_IT_SAYS[row.key]}</span> : null}
							</div>
							<Switch
								checked={row.on === true}
								loading={busy === row.key}
								onChange={on => toggle(row, on)}
								aria-labelledby={`public-form-notice-${row.key}`}
								data-key={row.key}
							/>
						</div>
						{row.key === FORM_SUBMISSION_KEY && row.on === true ? (
							<div className="public-form-notices__row public-form-notices__row--mine">
								<div className="public-form-notices__text">
									<span className="public-form-notices__name" id="public-form-notice-email">Email me too</span>
									<span className="public-form-notices__meta">Just for you. Others here decide for themselves.</span>
								</div>
								<Switch
									checked={emailOn === true}
									disabled={emailOn === null}
									loading={busy === 'email'}
									onChange={toggleEmail}
									aria-labelledby="public-form-notice-email"
									data-key="form-submission-email"
								/>
							</div>
						) : null}
					</li>
				))}
			</ul>
		</section>
	);
}
